import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { formatEther } from "@/helper/viem";

import { getTokenBalance } from "@/helper/contractFunctions";
import { getSwapTokens } from "@/helper/contractFunctions/MafiaExchange";
import { claimPerkBoxes } from "@/helper/contractFunctions/MafiaPerkBoxClaim";
import {
  buyPerkBoxes,
  splitPerkBoxes,
} from "@/helper/contractFunctions/MafiaPerkBoxMinter";
import {
  finishOpenPerkBox,
  requestOpenPerkBox,
} from "@/helper/contractFunctions/MafiaPerkOpener";
import {
  activatePerk,
  cancelPerk,
  getActivePerkInfo,
  unlockStake,
} from "@/helper/contractFunctions/MafiaPerkManager";

import { SwapToken } from "@/types/Contract/SwapToken";
import { ActivePerk, PendingStake } from "@/types/MafiaPerkManager";
import {
  getActivePerks,
  getUserPendingStakes,
  getUserStakeIds,
} from "@/helper/contractFunctions/MafiaPerkManager";

export interface PerkType {
  categoryId: number;
  hasActivePerk: boolean;
  effectStrength: number;
  duration: number;
  remainingDuration: number;
  slotIndex: number;
}

export interface PerkState {
  swapTokenInfo: [SwapToken[], number[]];
  isLoadingSwapTokenInfo: boolean;

  swapTokenBalances: any;
  isLoadingSwapTokenBalances: boolean;

  isBuyingPerkBox: boolean;
  isClaimingPerkBox: boolean;

  isRequestOpenPerkBox: boolean;
  isFinishOpenPerkBox: boolean;

  activePerksInfo: {
    activePerks: ActivePerk[];
    slotIndices: number[];
  };

  isLoadingActivePerks: boolean;

  userPendingStakes: PendingStake[];

  userStakeIds: number[];

  isLoadingUserPendingStakes: boolean;
  isLoadingUserStakeIds: boolean;

  // Crime perks
  perksInfo: PerkType[];

  isActivatingPerk: boolean;
  isCancellingPerk: boolean;
  isUnlockingStake: boolean;
  isSplittingPerkBox: boolean;
}

const initialState: PerkState = {
  swapTokenInfo: [[], []],
  isLoadingSwapTokenInfo: false,

  swapTokenBalances: {},
  isLoadingSwapTokenBalances: false,

  isBuyingPerkBox: false,
  isClaimingPerkBox: false,

  isRequestOpenPerkBox: false,
  isFinishOpenPerkBox: false,

  activePerksInfo: {
    activePerks: [],
    slotIndices: [],
  },

  isLoadingActivePerks: false,

  userPendingStakes: [],

  userStakeIds: [],

  isLoadingUserPendingStakes: false,
  isLoadingUserStakeIds: false,

  // Crime perks
  perksInfo: [],

  isActivatingPerk: false,
  isCancellingPerk: false,

  isUnlockingStake: false,

  isSplittingPerkBox: false,
};

export const buyPerkBoxesAction = createAsyncThunk(
  "perk/buyPerkBoxesAction",
  async ({
    swapTokenId,
    tokenAmount,
    tokenAddress,
    amount,
    account,
  }: {
    swapTokenId: number;
    tokenAmount: number;
    tokenAddress: string;
    amount: number;
    account: string;
  }) => {
    await buyPerkBoxes(swapTokenId, tokenAddress, tokenAmount, amount, account);
  }
);

export const getSwapTokenBalance = createAsyncThunk(
  "perk/getSwapTokenBalance",
  async ({ account }: { account: string }, { getState }) => {
    const state = (getState() as any).perk as PerkState;

    const balanceArray = await Promise.all(
      state.swapTokenInfo[0].map(async (token) => {
        const balance = await getTokenBalance(token.tokenAddress, account);
        return { [token.name]: balance };
      })
    );

    const balances = balanceArray.reduce((acc, entry) => {
      const key = Object.keys(entry)[0];
      acc[key] = entry[key];
      return acc;
    }, {});

    return balances;
  }
);

export const getSwapTokenInfo = createAsyncThunk(
  "perk/getSwapTokenInfo",
  async () => {
    const data = await getSwapTokens();

    const swapTokenList = data[0].map((tokenInfo) => {
      return {
        name: tokenInfo.name,
        tokenAddress: tokenInfo.tokenAddress,
        isStable: tokenInfo.isStable,
        isEnabled: tokenInfo.isEnabled,
        price: Number(tokenInfo.price),
        decimal: Number(tokenInfo.decimal),
      } as SwapToken;
    });

    const swapTokenPrice = data[1].map((price) => {
      return Number(formatEther(price));
    });

    return { swapTokenList, swapTokenPrice };
  }
);

export const claimPerkBoxesAction = createAsyncThunk(
  "perk/claimPerkBoxesAction",
  async ({
    account,
    itemIds,
    ogNFTAmount,
    signMsg,
    signature,
  }: {
    account: string;
    itemIds: number[];
    ogNFTAmount: number;
    signMsg: string;
    signature: string;
  }) => {
    await claimPerkBoxes(account, itemIds, ogNFTAmount, signMsg, signature);
  }
);

export const requestOpenPerkBoxAction = createAsyncThunk(
  "perk/requestOpenPerkBoxAction",
  async ({ account, itemId }: { account: string; itemId: number }) => {
    await requestOpenPerkBox(account, itemId);
  }
);

export const finishOpenPerkBoxAction = createAsyncThunk(
  "perk/finishOpenPerkBoxAction",
  async ({ account }: { account: string }) => {
    await finishOpenPerkBox(account);
  }
);

export const getActivePerksAction = createAsyncThunk(
  "perk/getActivePerksAction",
  async ({ account }: { account: string }) => {
    const activePerks = await getActivePerks(account);
    return activePerks;
  }
);

export const getUserPendingStakesAction = createAsyncThunk(
  "perk/getUserPendingStakesAction",
  async ({ account }: { account: string }) => {
    const userPendingStakes = await getUserPendingStakes(account);
    return userPendingStakes;
  }
);

export const getUserStakeIdsAction = createAsyncThunk(
  "perk/getUserStakeIdsAction",
  async ({ account }: { account: string }) => {
    const userStakeIds = await getUserStakeIds(account);
    return userStakeIds;
  }
);

export const getPerksInfoAction = createAsyncThunk(
  "perk/getPerksInfoAction",
  async ({
    account,
    categoryIds,
  }: {
    account: string;
    categoryIds: number[];
  }) => {
    const perks = await getActivePerkInfo(account, categoryIds);
    return perks;
  }
);

export const activatePerkAction = createAsyncThunk(
  "perk/activatePerkAction",
  async ({
    account,
    itemId,
    slotIndex,
  }: {
    account: string;
    itemId: number;
    slotIndex: number;
  }) => {
    await activatePerk(account, itemId, slotIndex);
  }
);

export const cancelPerkAction = createAsyncThunk(
  "perk/cancelPerkAction",
  async ({ account, slotIndex }: { account: string; slotIndex: number }) => {
    await cancelPerk(account, slotIndex);
  }
);

export const unlockStakeAction = createAsyncThunk(
  "perk/unlockStakeAction",
  async ({ account, stakeIndex }: { account: string; stakeIndex: number }) => {
    await unlockStake(account, stakeIndex);
  }
);

export const splitPerkBoxAction = createAsyncThunk(
  "perk/splitPerkBoxAction",
  async ({ account, itemIds }: { account: string; itemIds: number[] }) => {
    await splitPerkBoxes(account, itemIds);
  }
);

export const perkSlice = createSlice({
  name: "perk",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getSwapTokenBalance.pending, (state) => {
      state.isLoadingSwapTokenBalances = true;
    });
    builder.addCase(getSwapTokenBalance.fulfilled, (state, { payload }) => {
      state.swapTokenBalances = payload;
      state.isLoadingSwapTokenBalances = false;
    });
    builder.addCase(getSwapTokenBalance.rejected, (state, { error }) => {
      state.isLoadingSwapTokenBalances = false;
      console.log(error);
    });

    builder.addCase(getSwapTokenInfo.pending, (state) => {
      state.isLoadingSwapTokenInfo = true;
    });
    builder.addCase(getSwapTokenInfo.fulfilled, (state, { payload }) => {
      state.swapTokenInfo = [payload.swapTokenList, payload.swapTokenPrice];
      state.isLoadingSwapTokenInfo = false;
    });
    builder.addCase(getSwapTokenInfo.rejected, (state, { error }) => {
      state.isLoadingSwapTokenInfo = false;
      console.log(error);
    });

    builder.addCase(claimPerkBoxesAction.pending, (state) => {
      state.isClaimingPerkBox = true;
    });
    builder.addCase(claimPerkBoxesAction.fulfilled, (state) => {
      state.isClaimingPerkBox = false;
    });
    builder.addCase(claimPerkBoxesAction.rejected, (state, { error }) => {
      state.isClaimingPerkBox = false;
      console.log(error);
    });

    builder.addCase(requestOpenPerkBoxAction.pending, (state) => {
      state.isRequestOpenPerkBox = true;
    });
    builder.addCase(requestOpenPerkBoxAction.fulfilled, (state) => {
      state.isRequestOpenPerkBox = false;
    });
    builder.addCase(requestOpenPerkBoxAction.rejected, (state, { error }) => {
      state.isRequestOpenPerkBox = false;
      console.log(error);
    });

    builder.addCase(finishOpenPerkBoxAction.pending, (state) => {
      state.isFinishOpenPerkBox = true;
    });
    builder.addCase(finishOpenPerkBoxAction.fulfilled, (state) => {
      state.isFinishOpenPerkBox = false;
    });
    builder.addCase(finishOpenPerkBoxAction.rejected, (state, { error }) => {
      state.isFinishOpenPerkBox = false;
      console.log(error);
    });

    builder.addCase(getActivePerksAction.pending, (state) => {
      state.isLoadingActivePerks = true;
    });
    builder.addCase(getActivePerksAction.fulfilled, (state, { payload }) => {
      state.activePerksInfo = payload;
      state.isLoadingActivePerks = false;
    });
    builder.addCase(getActivePerksAction.rejected, (state, { error }) => {
      state.isLoadingActivePerks = false;
      console.log(error);
    });

    builder.addCase(getUserPendingStakesAction.pending, (state) => {
      state.isLoadingUserPendingStakes = true;
    });
    builder.addCase(
      getUserPendingStakesAction.fulfilled,
      (state, { payload }) => {
        state.userPendingStakes = payload;
        state.isLoadingUserPendingStakes = false;
      }
    );
    builder.addCase(getUserPendingStakesAction.rejected, (state, { error }) => {
      state.isLoadingUserPendingStakes = false;
      console.log(error);
    });

    builder.addCase(getUserStakeIdsAction.pending, (state) => {
      state.isLoadingUserStakeIds = true;
    });
    builder.addCase(getUserStakeIdsAction.fulfilled, (state, { payload }) => {
      state.userStakeIds = payload;
      state.isLoadingUserStakeIds = false;
    });
    builder.addCase(getUserStakeIdsAction.rejected, (state, { error }) => {
      state.isLoadingUserStakeIds = false;
      console.log(error);
    });

    builder.addCase(getPerksInfoAction.pending, (state) => {
      state.perksInfo = [];
    });
    builder.addCase(getPerksInfoAction.fulfilled, (state, { payload }) => {
      state.perksInfo = payload;
    });
    builder.addCase(getPerksInfoAction.rejected, (state, { error }) => {
      console.log(error);
    });

    builder.addCase(activatePerkAction.pending, (state) => {
      state.isActivatingPerk = true;
    });
    builder.addCase(activatePerkAction.fulfilled, (state) => {
      state.isActivatingPerk = false;
    });
    builder.addCase(activatePerkAction.rejected, (state, { error }) => {
      state.isActivatingPerk = false;
      console.log(error);
    });

    builder.addCase(cancelPerkAction.pending, (state) => {
      state.isCancellingPerk = true;
    });
    builder.addCase(cancelPerkAction.fulfilled, (state) => {
      state.isCancellingPerk = false;
    });
    builder.addCase(cancelPerkAction.rejected, (state, { error }) => {
      state.isCancellingPerk = false;
      console.log(error);
    });

    builder.addCase(unlockStakeAction.pending, (state) => {
      state.isUnlockingStake = true;
    });
    builder.addCase(unlockStakeAction.fulfilled, (state) => {
      state.isUnlockingStake = true;
    });
    builder.addCase(unlockStakeAction.rejected, (state, { error }) => {
      state.isUnlockingStake = false;
      console.log(error);
    });

    builder.addCase(splitPerkBoxAction.pending, (state) => {
      state.isSplittingPerkBox = true;
    });
    builder.addCase(splitPerkBoxAction.fulfilled, (state) => {
      state.isSplittingPerkBox = false;
    });
    builder.addCase(splitPerkBoxAction.rejected, (state, { error }) => {
      state.isSplittingPerkBox = false;
      console.log(error);
    });
  },
});

// eslint-disable-next-line no-empty-pattern
export const {} = perkSlice.actions;

export default perkSlice.reducer;
